import { CollisionGrid } from '../world/CollisionGrid.js';

/**
 * @file THE DOOR — every way in and out of a building, and nothing else.
 *
 * A storefront is not a room carved into a building: it is a prebuilt box
 * sitting 100 m UNDER the city (`level.interiors.floorY`), with its own
 * collision grid and its own dead-flat floor. Walking through a door is
 * therefore a TELEPORT with a curtain drawn over it, and this file is the
 * curtain:
 *
 * ```
 *  FADE_OUT ──▶ SWAP ──▶ FADE_IN
 *   0.30 s     1 step     0.30 s
 * ```
 *
 * Both directions run the same three phases; only {@link SWAP} knows which way
 * round it is. Entering swaps the mover's environment (grid + `heightAt`),
 * drops the player on the room's spawn mat and pulls the camera in; leaving
 * puts all three back and stands the player on the outdoor ring they came from.
 * SWAP is instantaneous — it exists as a phase because a state machine whose
 * work happens in a phase is one you can single-step in a test, which is
 * exactly how `systems/Carjack.js` is built and for the same reason.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS
 * ─────────────────────────────────────────────────────────────────────────────
 * Same deal as `traffic/Traffic.js`, `systems/Driving.js` and
 * `systems/Carjack.js`: the player, the camera rig, the bus, the level and the
 * outdoor height function are all INJECTED, so the whole transition — the
 * refusals, the phase timings, the environment swap, every abort — is provable
 * in node with a duck-typed player. `CollisionGrid` is the one import and it is
 * three-free by design (it is parallel typed arrays and arithmetic).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONE RULE
 * ─────────────────────────────────────────────────────────────────────────────
 * **`abort()` leaves a world where F was never pressed.** It is called on the
 * player's death, on teardown, and by anything that needs the outdoors back
 * right now. Whatever phase it lands in, it puts the mover's environment back,
 * stands the player on the outdoor ring, drops the camera out of interior
 * framing, lifts the curtain and clears the prompt. There is no phase from
 * which it leaves the player sealed in a box under the city — which is the only
 * failure mode this system has, and it is unrecoverable in play.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE THINGS THAT WILL BITE
 * ─────────────────────────────────────────────────────────────────────────────
 * • **The interior grid is ONE grid for EVERY room.** The rooms are laid out in
 *   a disjoint slot region far from the island, so the colliders of all of them
 *   can live in a single bucket grid and the swap is a pointer write rather
 *   than a build. Give two plans overlapping slots and they become one room.
 *
 * • **`focus` is the DOOR, not the player.** While the player is under the city
 *   every ring system that follows them — traffic, population, the wanted
 *   level, the spawner, the minimap — has to keep running around the SHOPFRONT,
 *   or the street empties while you are buying a sandwich and is still empty
 *   when you come out. Gameplay anchors those at `focus ?? player.position`.
 *
 * • **The near wall does NOT dissolve on its own.** `OcclusionFade` fades what
 *   is on the camera->player ray, and at 52 degrees of pitch that ray drops in
 *   over the top of a 3.4 m wall without touching it — so the wall stays solid
 *   and its inner face hides the counter and the shelving standing against it.
 *   While the player is in a room this file therefore FORCES the camera-side
 *   walls (`fade.setForced`), picked from the camera yaw alone because a room
 *   is axis-aligned. The pick is a change-gate, not a per-frame call.
 *
 * • **The prompt is EDGE-DETECTED and scanned at ~4 Hz.** It is a string
 *   crossing the engine/React seam on a bus channel React re-renders from; at
 *   60 Hz an unchanged prompt would be 60 wasted emits a second. The scan
 *   itself is a plain distance² sweep of `level.doors` (≤ ~80 of them) with a
 *   per-axis early reject, which is cheaper than any structure that would have
 *   to be kept in sync with the level.
 *
 *   ⚠ That is also why the INDOOR prompt has a SEAM (`extraPrompt`) rather than
 *   a second publisher. `systems/Shops.js` owns a till the player stands at,
 *   but a second system emitting on an edge-detected channel sees this one's
 *   string as a change and vice versa, and the HUD strobes between `null` and
 *   `'F — Snack $15'` at the scan rate. The rule: on the exit mat,
 *   `'F — Leave'`; inside and off the mat, whatever `extraPrompt()` says;
 *   outdoors, unchanged. THE MAT ALWAYS WINS — see the branch itself.
 *
 * @module engine/systems/Interiors
 */

/** @typedef {import('../types.js').Collider} Collider */

/**
 * @typedef {Object} Door The outdoor half — published as `level.doors`.
 * @property {string} id `'door.<lotId>'`
 * @property {string} buildingId
 * @property {number} x @property {number} z @property {number} yaw
 * @property {number} nx @property {number} nz Outward wall normal.
 * @property {number} w @property {number} seed
 * @property {{x: number, z: number}} ring Where the player stands to press F.
 */

/**
 * @typedef {Object} InteriorPlan The indoor half — `level.interiors.plans[]`.
 * @property {string} id `'int.<lotId>'`
 * @property {string} buildingId @property {string} doorId
 * @property {number} seed
 * @property {{x: number, z: number}} slot
 * @property {number} floorY @property {number} w @property {number} d
 * @property {{x: number, z: number}} spawn Where the player lands inside.
 * @property {{x: number, z: number}} exit ...and where F sends them back out.
 * @property {any[]} [lampEmitters]
 * @property {Collider[]} colliders
 * @property {any[]} [fadeVolumes]
 * @property {string} [kind] What the room IS — `'grocer'`/`'diner'`/`'gunsmith'`.
 *   Drawn from die ONE of the room stream; it picks the fixtures, the stock
 *   palette and the street sign.
 * @property {string} [trade] What the room SELLS, if that differs from what it
 *   LOOKS like. `Shops.js` reads `trade ?? kind` as its catalogue key, and
 *   nothing sets this today.
 *   ⚠ It exists so a future shop (a weapons dealer, an outfitter) can reuse an
 *   EXISTING fixture set with a new catalogue at zero cost. Adding a real
 *   `kind` is the expensive move: `SHOP_KINDS` is drawn from die ONE, so a
 *   fourth kind reshuffles all 314 rooms, its fixture dice join the
 *   `DICE_PER_ROOM` 99 superset (moving the lamps on die 99), and the sign
 *   atlas needs 16 new names × 3 colourways. New CATALOGUES are free; new
 *   FIXTURE SETS are not.
 * @property {{keeper: {x: number, z: number, yaw: number},
 *   till: {x: number, z: number}, browse: {x: number, z: number}[]}} [stations]
 *   Pure derived data, spends no dice — the contract `Shops.js` spawns against.
 */

/**
 * Where the machine is. `IDLE` is 0 so a falsy check reads as "nothing is
 * happening"; the rest are the phase order in BOTH directions.
 * @enum {number}
 */
export const INTERIOR_PHASE = Object.freeze({
  IDLE: 0,
  /** The curtain comes down. Nothing has moved yet. */
  FADE_OUT: 1,
  /** One step, behind the black: the whole teleport. */
  SWAP: 2,
  /** The curtain goes up on the other world. */
  FADE_IN: 3,
});

/** Debug labels, indexed by {@link INTERIOR_PHASE}. */
export const INTERIOR_PHASE_NAMES = Object.freeze([
  'idle', 'fadeOut', 'swap', 'fadeIn',
]);

/**
 * Every number the transition owns. Seconds and metres.
 */
export const DOOR = Object.freeze({
  /**
   * Down to black. Long enough to read as a cut rather than a stutter, short
   * enough that a player walking a parade of shopfronts is not waiting on it.
   */
  fadeOut: 0.30,
  /** ...and back up. Symmetric on purpose: an asymmetric fade reads as a hitch. */
  fadeIn: 0.30,
  /**
   * How far F reaches for a door ring — outdoors from `door.ring`, indoors from
   * `plan.exit`. The ring already sits ~1.1 m out from the wall, so this is the
   * radius of the mat you stand on, not the reach of an arm.
   */
  reach: 2.2,
});

/** Prompt proximity scan period, seconds (~4 Hz). */
const PROMPT_PERIOD = 0.25;
/** The prompt strings. Exactly two, and `null` is the third state. */
const PROMPT_ENTER = 'F — Enter';
const PROMPT_LEAVE = 'F — Leave';
/** An abort lifts the curtain faster than a completed transition would. */
const ABORT_FADE_MS = 150;
/** Bucket size of the interior grid, metres. A room is a few metres across. */
const INTERIOR_CELL = 2;

/**
 * Wall order, and it is `levels/city/interiors.js`'s contract: `w0` is the EXIT
 * wall (−Z), `w1` is +Z, `w2` is −X, `w3` is +X, and the fade volume ids are
 * `'<planId>.w0'`..`'.w3'`. Only used as a FALLBACK — the plan publishes the
 * ids on `fadeVolumes` and those are read first.
 * @type {ReadonlyArray<string>}
 */
const WALL_ID = Object.freeze(['w0', 'w1', 'w2', 'w3']);

/**
 * The camera-side wall test, with hysteresis.
 *
 * `Engine#updateCamera` puts the camera at `target + (sin yaw, ·, cos yaw) * d`,
 * so the horizontal direction from the room to the camera is exactly
 * `(sin yaw, cos yaw)` — and a room is axis-aligned, so "which walls stand
 * between the camera and the player" is that vector's sign per axis. One wall
 * per axis, never more: forcing all four would dissolve the far walls too and
 * the player would be looking at the void under the world through them.
 *
 * The band is what stops a wall standing nearly EDGE-ON to the view from being
 * dissolved for nothing — at the level's default yaw of 45 degrees both
 * components are 0.707 and both walls go, while at a 90-degree snap one
 * component is 0 and only the wall squarely in the way does. Two thresholds
 * rather than one because the rig's yaw damping overshoots its snap target: a
 * single edge would chatter a wall in and out of the forced set right where the
 * camera settles, and the fader would pulse it at the ramp rate.
 */
const FACE_ENTER = 0.25;
/** ...and the wall is only released once the axis falls back under this. */
const FACE_LEAVE = 0.10;

/**
 * The building transition. Owns F when the player is on foot near a door, and
 * nothing else — `systems/Carjack.js` owns the same key beside a car, and the
 * two never both claim a press because a door ring is never inside a car.
 *
 * @param {Object} o
 * @param {any} o.player {@link import('../actors/Player.js').Player}
 * @param {any} [o.rig] {@link import('../actors/CameraRig.js').CameraRig}
 * @param {any} [o.bus] `'screen:fade'`, `'player:interior'`, `'hud:prompt'`.
 * @param {any} o.level Read for `doors` and `interiors`. Without either the
 *   system is inert and `press()` is always false.
 * @param {any} [o.driving] `createDriving`'s system — a player in (or climbing
 *   into) a car cannot walk through a door.
 * @param {any} [o.carjack] `createCarjack`'s system — nor can one mid-jack.
 * @param {() => boolean} [o.canEnter] Veto for ENTERING only (a mission that
 *   needs the player outdoors, a wanted level, a cutscene). Leaving is never
 *   vetoed: the exit is the only way out of the box.
 * @param {() => (string|null)} [o.extraPrompt] The INDOOR prompt seam.
 *   Consulted ONLY while the player is inside a room and NOT standing on the
 *   exit mat — `systems/Shops.js`'s till is the one caller, and it is wired
 *   through here rather than publishing for itself because `'hud:prompt'` is
 *   EDGE-DETECTED: two publishers on one edge-detected channel each see the
 *   other's string as a change and the HUD flickers between them at the scan
 *   rate. One publisher, one edge. Defaults to `() => null`, which makes this
 *   file behave exactly as it did before the seam existed.
 * @param {(x: number, z: number) => number} [o.heightAt] The OUTDOOR height
 *   function, used for the drop-off. Injected because this file must not reach
 *   into the level's internals.
 * @param {any} [o.occlusionFade] `createOcclusionFade`'s system, for the
 *   camera-side wall forcing. OPTIONAL and looked up LAZILY: when it is absent
 *   the level's own `level.occlusionFade` is used (the city sets it during the
 *   build, and a level may drop it on teardown), and when there is neither —
 *   every dev scene, every test — the whole feature is simply not there. The
 *   yaw comes from `rig.yaw`, which is a plain number; no three.js enters this
 *   file for any of it.
 * @returns {Object}
 */
export function createInteriors(o) {
  const {
    rig = null,
    bus = null,
    level = null,
    driving = null,
    carjack = null,
    canEnter = () => true,
    extraPrompt = null,
    heightAt = () => 0,
    occlusionFade = null,
  } = o ?? {};
  // Rebindable on purpose: Gameplay hot-swaps the Player once its sprite
  // decodes (same reasoning as Driving.js#setPlayer / Carjack.js#setPlayer).
  let player = o?.player;

  if (!player) throw new Error('createInteriors: player is required');

  /**
   * The indoor prompt seam, normalised once. A caller that passes nothing (or
   * something that is not callable) gets the constant `null` and this file is
   * byte-identical in behaviour to the version without the option.
   * @type {() => (string|null)}
   */
  const indoorPrompt = typeof extraPrompt === 'function' ? extraPrompt : () => null;

  // ── the level's two halves, indexed once ──────────────────────────────────
  /** @type {Door[]} */
  const allDoors = Array.isArray(level?.doors) ? level.doors : [];
  /** @type {InteriorPlan[]} */
  const plans = Array.isArray(level?.interiors?.plans) ? level.interiors.plans : [];
  /** Fallback floor for the height closure before/after any plan is current. */
  const baseFloorY = level?.interiors?.floorY ?? -100;

  /** @type {Map<string, Door>} */
  const doorById = new Map();
  for (const d of allDoors) if (d?.id) doorById.set(d.id, d);
  /** @type {Map<string, InteriorPlan>} */
  const planByDoor = new Map();
  for (const p of plans) if (p?.doorId) planByDoor.set(p.doorId, p);

  /**
   * The doors F actually reaches for: only those with a room behind them. A
   * door with no plan is scenery, and offering to enter it is a bug the player
   * finds before we do.
   * @type {Door[]}
   */
  const liveDoors = allDoors.filter((d) => d?.ring && planByDoor.has(d.id));

  /**
   * ONE grid for EVERY room — the rooms live in a disjoint slot region, so
   * their colliders never see each other and the swap costs a pointer write.
   * Built at construction rather than lazily: it is a few hundred AABBs, and a
   * first-entry hitch is exactly the frame the player is watching.
   * @type {CollisionGrid|null}
   */
  let grid = null;
  if (plans.length && level?.interiors?.region) {
    grid = new CollisionGrid(level.interiors.region, INTERIOR_CELL);
    for (const p of plans) if (Array.isArray(p?.colliders)) grid.addColliders(p.colliders);
  }
  /** Nothing to enter: the machine is inert but every method is still callable. */
  const inert = !grid || liveDoors.length === 0;

  // ── the machine ───────────────────────────────────────────────────────────
  let phase = INTERIOR_PHASE.IDLE;
  /** `'enter'` (into a room) or `'exit'` (out of one). */
  let mode = 'enter';
  let timer = 0;
  let elapsed = 0;
  let disposed = false;
  let entries = 0;

  /** The room the player is IN, or null. Set at SWAP, cleared at SWAP. */
  /** @type {InteriorPlan|null} */
  let room = null;
  /** The plan this transition is about — the destination, or the room we leave. */
  /** @type {InteriorPlan|null} */
  let targetPlan = null;
  /** ...and its door. The outdoor anchor for the whole sequence. */
  /** @type {Door|null} */
  let targetDoor = null;
  /** True while the mover's environment is OURS. Decides how `abort` unwinds. */
  let swapped = false;

  /** Last prompt PUBLISHED. Edge detection, not a redraw. */
  /** @type {string|null} */
  let prompt = null;
  /** Accumulator for the ~4 Hz proximity scan. */
  let promptT = PROMPT_PERIOD;

  // ── the camera-side walls (see the header) ────────────────────────────────
  /** Which +Z/−Z wall is forced: 0 none, +1 `w1` (+Z), −1 `w0` (−Z). Latched
   *  across the hysteresis band, hence state rather than a pure function. */
  let faceZ = 0;
  /** ...and which ±X wall: 0 none, +1 `w3` (+X), −1 `w2` (−X). */
  let faceX = 0;
  /** The room whose walls were last handed to the fader. `null` = none are.
   *  Together with `sentZ`/`sentX` this is the CHANGE GATE: the pick runs every
   *  step (ten flops), the fader is only told when the answer moves.
   *  @type {InteriorPlan|null} */
  let sentRoom = null;
  let sentZ = 0;
  let sentX = 0;

  // ── scratch (allocated ONCE — a per-step machine must not litter) ─────────
  const focusPt = { x: 0, z: 0 };
  /** The id list handed to `setForced`, which COPIES it. Reused every call. */
  /** @type {string[]} */
  const forcedWalls = [];
  /**
   * The interior floor sampler. ONE closure for every room: it reads whichever
   * plan is current, so entering a second room does not allocate a second
   * function and a stale copy can never outlive its plan.
   */
  const interiorHeight = () => (room ? room.floorY : baseFloorY);

  // ── helpers ───────────────────────────────────────────────────────────────

  /** @param {string} type @param {any} payload */
  const emit = (type, payload) => {
    bus?.emit?.(type, payload);
  };

  /**
   * Publishes a prompt only when it CHANGES.
   * @param {string|null} text
   */
  const setPrompt = (text) => {
    if (prompt === text) return;
    prompt = text;
    emit('hud:prompt', { text });
  };

  /** @param {number} seconds @returns {number} Milliseconds, for the UI. */
  const ms = (seconds) => Math.round(seconds * 1000);

  // ── the camera-side walls ─────────────────────────────────────────────────

  /**
   * The fader, or null. Looked up on every use rather than captured: the city
   * sets `level.occlusionFade` during its build and clears it on teardown, and
   * this system outlives neither event reliably.
   * @returns {any}
   */
  const fader = () => occlusionFade ?? level?.occlusionFade ?? null;

  /**
   * @param {InteriorPlan} plan @param {number} i {@link WALL_ID} index.
   * @returns {string} The fade-volume id of that wall. The plan's own record is
   *   authoritative; the template is the fallback for a plan that publishes
   *   colliders but no volumes.
   */
  const wallId = (plan, i) => plan.fadeVolumes?.[i]?.id ?? `${plan.id}.${WALL_ID[i]}`;

  /** Stops forcing anything. Idempotent, and silent when nothing was forced. */
  const releaseWalls = () => {
    faceZ = 0;
    faceX = 0;
    if (!sentRoom) return;
    sentRoom = null;
    sentZ = 0;
    sentX = 0;
    forcedWalls.length = 0;
    fader()?.clearForced?.();
  };

  /**
   * Picks the 1-2 walls standing between the camera and the room and forces
   * them to dissolve. Cheap enough to run every step; the fader only hears
   * about it when the pick actually changes.
   *
   * Degrades to a no-op — never a throw — with no fader, no rig, or a rig that
   * does not publish a finite `yaw`.
   */
  const syncWalls = () => {
    if (!room) {
      releaseWalls();
      return;
    }
    const f = fader();
    const yaw = rig?.yaw;
    if (!f?.setForced || typeof yaw !== 'number' || !Number.isFinite(yaw)) return;

    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    if (c > FACE_ENTER) faceZ = 1;
    else if (c < -FACE_ENTER) faceZ = -1;
    else if (c < FACE_LEAVE && c > -FACE_LEAVE) faceZ = 0;
    if (s > FACE_ENTER) faceX = 1;
    else if (s < -FACE_ENTER) faceX = -1;
    else if (s < FACE_LEAVE && s > -FACE_LEAVE) faceX = 0;

    if (room === sentRoom && faceZ === sentZ && faceX === sentX) return;
    sentRoom = room;
    sentZ = faceZ;
    sentX = faceX;

    forcedWalls.length = 0;
    if (faceZ > 0) forcedWalls.push(wallId(room, 1));
    else if (faceZ < 0) forcedWalls.push(wallId(room, 0));
    if (faceX > 0) forcedWalls.push(wallId(room, 3));
    else if (faceX < 0) forcedWalls.push(wallId(room, 2));
    f.setForced(forcedWalls);
  };

  /**
   * @returns {boolean} Something else owns the player right now — a car, a
   *   climb into one, a carjack, or death. Shared by `press` and the prompt, so
   *   the offer and the acceptance can never disagree.
   */
  const busy = () => {
    if (player.dead) return true;
    if (driving?.inCar) return true;
    if (driving && driving.phase !== 'foot') return true;
    // `Carjack` exposes BOTH `active` (a sequence is running) and `locked` (the
    // player's own movement is suspended, which goes false the moment they are
    // driving). `active` is the right one: the door must stay shut for the
    // whole sequence, including the CLOSE phase the driver sits through.
    if (carjack && (carjack.active ?? carjack.locked)) return true;
    return false;
  };

  /**
   * Nearest door ring within {@link DOOR.reach} of a point. Plain distance²
   * over ≤ ~80 doors with a per-axis early reject — no structure to keep in
   * sync with the level.
   * @param {number} x @param {number} z
   * @returns {Door|null}
   */
  const doorNear = (x, z) => {
    const r = DOOR.reach;
    const r2 = r * r;
    /** @type {Door|null} */
    let best = null;
    let bestD2 = r2;
    for (let i = 0; i < liveDoors.length; i++) {
      const d = liveDoors[i];
      const dx = d.ring.x - x;
      if (dx > r || dx < -r) continue;
      const dz = d.ring.z - z;
      if (dz > r || dz < -r) continue;
      const d2 = dx * dx + dz * dz;
      if (d2 >= bestD2) continue;
      bestD2 = d2;
      best = d;
    }
    return best;
  };

  /**
   * @param {InteriorPlan|null} plan
   * @returns {boolean} The player is standing on this room's exit mat.
   */
  const onExitMat = (plan) => {
    if (!plan?.exit) return false;
    const dx = plan.exit.x - player.position.x;
    const dz = plan.exit.z - player.position.z;
    return dx * dx + dz * dz <= DOOR.reach * DOOR.reach;
  };

  /** Everything back to rest. Does NOT touch the world. */
  const finish = () => {
    phase = INTERIOR_PHASE.IDLE;
    timer = 0;
    targetPlan = null;
    targetDoor = null;
  };

  // ── the swap ──────────────────────────────────────────────────────────────

  /**
   * Puts the player under the city. Everything in one step, behind the black:
   * the mover's environment, its position, the camera framing and the two
   * broadcasts the rest of the game reacts to.
   * @param {InteriorPlan} plan @param {Door} door
   */
  const swapIn = (plan, door) => {
    // `room` FIRST: `interiorHeight` reads it, and the mover may sample the
    // floor on the very next step.
    room = plan;
    swapped = true;
    player.setEnvironment({ collision: grid, heightAt: interiorHeight });
    // The room's own pre-solved probe (`plan.light`, solved by the level at
    // build) — the grid the player normally samples clamps to street level and
    // cannot light anyone at -100. Optional-chained twice over: an old plan
    // without `light`, or a player without the method, degrades to the grid
    // sample rather than to a throw mid-swap.
    if (plan.light) player.setLightOverride?.(plan.light);
    player.moveTo(plan.spawn.x, plan.spawn.z, plan.floorY);
    rig?.setInteriorMode?.(true);
    rig?.snapTo?.(player.position);
    // Behind the black, so the near wall is already a ghost when the curtain
    // lifts rather than dissolving in front of the player afterwards.
    syncWalls();
    entries++;
    emit('player:interior', { in: true, buildingId: door.buildingId });
  };

  /**
   * ...and back out. The mirror of {@link swapIn}, and the only path that
   * clears `room` — `abort` routes through it rather than repeating it, which
   * is what stops the two ways out of a building drifting apart.
   * @param {Door} door
   */
  const swapOut = (door) => {
    room = null;
    swapped = false;
    // Before anything else: a wall left forced would go on dissolving a room
    // nobody is in, for as long as the level lives.
    releaseWalls();
    player.setLightOverride?.(null);
    player.clearEnvironment?.();
    const rx = door.ring.x;
    const rz = door.ring.z;
    player.moveTo(rx, rz, heightAt(rx, rz));
    rig?.setInteriorMode?.(false);
    rig?.snapTo?.(player.position);
    emit('player:interior', { in: false, buildingId: door.buildingId });
  };

  // ── abort ─────────────────────────────────────────────────────────────────

  /**
   * Gives up wherever it is and leaves the outdoors exactly as a world where F
   * was never pressed. Called on the player's death, on `dispose`, and by
   * anything that needs the street back now.
   *
   * The unwind depends on ONE thing: is the mover's environment ours?
   *  • No — nothing has moved. Lift the curtain and forget the sequence.
   *  • Yes — the player is under the city and there is no honest way to leave
   *    them there. Run the exit swap against the door we came in through.
   *
   * From a genuine standstill (IDLE, outdoors) it is a no-op beyond clearing a
   * stale prompt: an un-fade emitted at rest would be a curtain lifting on a
   * screen nobody covered.
   */
  const abort = () => {
    // Unconditional and first: "a world where F was never pressed" includes the
    // fader's forced set, and this is a no-op when nothing was forced.
    releaseWalls();
    if (phase === INTERIOR_PHASE.IDLE && !swapped) {
      setPrompt(null);
      return;
    }
    if (swapped) {
      const door = targetDoor ?? (room ? doorById.get(room.doorId) ?? null : null);
      if (door) swapOut(door);
      else {
        // The door went out from under us (a level swapped its data). There is
        // no ring to stand on, but the environment MUST still come back, or the
        // player walks the city with a room's walls around them.
        room = null;
        swapped = false;
        player.setLightOverride?.(null);
        player.clearEnvironment?.();
        rig?.setInteriorMode?.(false);
        emit('player:interior', { in: false, buildingId: null });
      }
    }
    emit('screen:fade', { black: false, ms: ABORT_FADE_MS });
    setPrompt(null);
    mode = 'enter';
    finish();
  };

  // ── the F key ─────────────────────────────────────────────────────────────

  /**
   * THE F KEY, when the player is on foot at a door. Claims the press or
   * declines it — `Gameplay` offers it here first and falls through to
   * `Carjack` on a false, because a door ring and a car door are never the same
   * spot but the key is the same key.
   * @returns {boolean} True when a transition began.
   */
  const press = () => {
    if (disposed || inert) return false;
    if (phase !== INTERIOR_PHASE.IDLE) return false;
    if (busy()) return false;

    if (room) {
      // OUT. Never vetoed: `canEnter` guards the street, and refusing the exit
      // would seal the player in a box with no other way out.
      if (!onExitMat(room)) return false;
      const door = doorById.get(room.doorId);
      if (!door?.ring) return false;
      mode = 'exit';
      targetPlan = room;
      targetDoor = door;
    } else {
      if (!canEnter()) return false;
      const door = doorNear(player.position.x, player.position.z);
      if (!door) return false;
      const plan = planByDoor.get(door.id);
      if (!plan) return false;
      mode = 'enter';
      targetPlan = plan;
      targetDoor = door;
    }

    phase = INTERIOR_PHASE.FADE_OUT;
    timer = DOOR.fadeOut;
    elapsed = 0;
    setPrompt(null);
    emit('screen:fade', { black: true, ms: ms(DOOR.fadeOut) });
    return true;
  };

  // ── the step ──────────────────────────────────────────────────────────────

  /**
   * One fixed step. Runs at IDLE too — the proximity prompt is its other job,
   * and it is the only thing that tells the player a door is a door.
   * @param {number} dt
   */
  const update = (dt) => {
    if (disposed || !(dt > 0)) return;

    // The camera yaw is FREE, so which wall is in the way changes under a
    // stationary player. Runs in every phase and outdoors too — outdoors it is
    // one branch, and it is what guarantees nothing stays forced.
    syncWalls();

    if (phase !== INTERIOR_PHASE.IDLE) {
      elapsed += dt;
      // Dying mid-transition is the one interrupt. Everything else — damage, a
      // wanted level arriving, a mission starting — happens behind the black
      // and is somebody else's problem when the curtain lifts.
      if (player.dead) {
        abort();
        return;
      }

      switch (phase) {
        case INTERIOR_PHASE.FADE_OUT: {
          timer -= dt;
          // The swap gets its OWN step. It costs one frame of black nobody can
          // see and it buys a phase a test can stop on.
          if (timer <= 0) {
            phase = INTERIOR_PHASE.SWAP;
            timer = 0;
          }
          break;
        }

        case INTERIOR_PHASE.SWAP: {
          const plan = targetPlan;
          const door = targetDoor;
          if (!plan || !door) {
            abort();
            return;
          }
          if (mode === 'enter') swapIn(plan, door);
          else swapOut(door);
          emit('screen:fade', { black: false, ms: ms(DOOR.fadeIn) });
          phase = INTERIOR_PHASE.FADE_IN;
          timer = DOOR.fadeIn;
          break;
        }

        case INTERIOR_PHASE.FADE_IN: {
          timer -= dt;
          if (timer <= 0) finish();
          break;
        }

        default:
          finish();
          break;
      }
      return;
    }

    // ── IDLE: the prompt ──
    if (inert || busy()) {
      setPrompt(null);
      promptT = PROMPT_PERIOD;
      return;
    }
    promptT += dt;
    if (promptT < PROMPT_PERIOD) return;
    promptT = 0;

    if (room) {
      // THE EXIT MAT ALWAYS WINS. The till and the door are never the same spot
      // (a plan's `till` is the customer side of a counter and its `exit` is the
      // way out), but if a level ever put them within a mat's width of each
      // other the offer that must survive is the one that leads OUT: refusing
      // the way out is the only unrecoverable state this system has, and a
      // prompt that hides it is the first half of that bug.
      if (onExitMat(room)) {
        setPrompt(PROMPT_LEAVE);
        return;
      }
      // Off the mat, inside a room: somebody else may have something to say.
      const extra = indoorPrompt();
      setPrompt(typeof extra === 'string' && extra ? extra : null);
      return;
    }
    // The offer has to match what `press` would actually do, `canEnter` and
    // all: a prompt for a key that refuses is worse than no prompt.
    if (!canEnter()) {
      setPrompt(null);
      return;
    }
    setPrompt(doorNear(player.position.x, player.position.z) ? PROMPT_ENTER : null);
  };

  return {
    name: 'interiors',
    press,
    update,
    abort,

    /**
     * Gameplay's player hot-swap points us at the replacement actor. If the
     * replacement was built outdoors while we are in a room, it arrives with
     * the CITY's collision and the terrain's height — so the environment goes
     * straight back on, and its Y with it.
     * @param {any} p
     */
    setPlayer(p) {
      player = p;
      if (!p) return;
      if (swapped && room) {
        p.setEnvironment?.({ collision: grid, heightAt: interiorHeight });
        // The replacement was lit outdoors too — same reasoning as the
        // environment line above, or the swap-in sprite goes street-dark.
        if (room.light) p.setLightOverride?.(room.light);
        p.moveTo?.(p.position.x, p.position.z, room.floorY);
      }
    },

    /**
     * @returns {boolean} The player's own movement is suspended. True for the
     *   whole transition — Gameplay's one-mover rule, and there is nothing to
     *   walk on for part of it anyway.
     */
    get locked() {
      return phase !== INTERIOR_PHASE.IDLE;
    },

    /** @returns {boolean} The player is under the city. */
    get inside() {
      return room !== null;
    },

    /** @returns {InteriorPlan|null} The room the player is in, or null. */
    get plan() {
      return room;
    },

    /**
     * @returns {{x: number, z: number}|null} Where the WORLD should think the
     *   player is: the shopfront's outdoor ring, for as long as they are inside
     *   or on their way in or out. Null outdoors, which is the caller's cue to
     *   use `player.position`. A copy, not the level's own object.
     */
    get focus() {
      const door = targetDoor ?? (room ? doorById.get(room.doorId) ?? null : null);
      if (!door?.ring) return null;
      focusPt.x = door.ring.x;
      focusPt.z = door.ring.z;
      return focusPt;
    },

    /** @returns {number} {@link INTERIOR_PHASE} */
    get phase() {
      return phase;
    },

    /** @returns {string} */
    get phaseName() {
      return INTERIOR_PHASE_NAMES[phase] ?? 'idle';
    },

    /** @returns {string|null} The prompt currently published. */
    get prompt() {
      return prompt;
    },

    /** @returns {CollisionGrid|null} The one grid every room shares. */
    get collision() {
      return grid;
    },

    /**
     * @returns {string[]} The wall fade-volume ids currently forced to dissolve
     *   — 1-2 while inside a room, empty otherwise. A copy.
     */
    get forcedWalls() {
      return sentRoom ? forcedWalls.slice() : [];
    },

    /** @returns {{phase: string, mode: string, elapsed: number, doors: number, rooms: number, entries: number, inside: string|null, forced: string[]}} */
    stats() {
      return {
        phase: INTERIOR_PHASE_NAMES[phase] ?? 'idle',
        mode,
        elapsed: Math.round(elapsed * 100) / 100,
        doors: liveDoors.length,
        rooms: plans.length,
        entries,
        inside: room ? room.id : null,
        forced: sentRoom ? forcedWalls.slice() : [],
      };
    },

    /** Idempotent. Puts the player back outdoors first. */
    dispose() {
      if (disposed) return;
      abort();
      disposed = true;
    },
  };
}
